import useGetStowagePlan from 'src/api/stowage/useGetStowageApi';
import { useRotationStore } from 'src/context/RotationContext';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import { useVesselStore } from 'src/context/VesselContext';
import { useViolationsStore } from 'src/context/ViolationsContext';
import { cleanStowagePlanData } from 'src/factory/Stowage/StowagePlanFactory';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import { getStowageDataItems } from 'src/utils/stowage/stowagePlanChangeUtils/stowageDataItemsBuilder';
import { QueryParamProps } from 'odfjell-query-manager';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import useUpdateStowagePlan from '../useUpdateStowagePlan';
import { useAppStore } from 'src/context/AppContext';
import StowagePlanNotFoundMessage from 'src/components/stowage/common/StowagePlanNotFoundMessage';
import React from 'react';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { useSelectCargoOperation } from '../useSelectCargoOperation';
import { getMasterStowagePlan } from 'src/utils/stowage/stowagePlanUtils';

export const useStowageUrlChangeHandler = () => {
    const { setSelectedCargoOperation, selectedCargoOperation } = useStowageSelectionStore();
    const { stowagePlans, setActiveStowagePlan, activeStowagePlan } = useStowagePlanStore();
    const { setSavedViolations } = useViolationsStore();
    const {
        setStowagePlanRotationPortCallList,
        setStowagePlanRotationCargoList,
        setSelectedStowageRotationBerthCall,
        setSelectedStowageRotationPortCall,
        selectedStowageRotationPortCall,
        selectedStowageRotationBerthCall,
        stowageRotationTree,
        selectedStowageVoyages,
        setSelectedStowageVoyages,
    } = useRotationStore();
    const { getStowagePlanChanges, getStowagePlans } = useGetStowagePlan();
    const { selectedVessel } = useVesselStore();
    const {
        setQueryParamStowagePlan,
        clearQueryParamsStowagePlan,
        clearQueryParamsStowagePortCall,
        clearQueryParamsStowageBerthCall,
        clearQueryParamsStowageCargoOperation,
    } = useStowageQueryParamsUtils();
    const { updateStowageData } = useUpdateStowagePlan();
    const { selectCargoOperation } = useSelectCargoOperation();
    const { setAppModalData } = useAppStore();

    const handleStowageCallQueryParamChanges = (queryParamUpdate: QueryParamProps, filteredStowageRotationTree: RotationPortCall[]) => {
        const getQueryParamValue = (name: string) => queryParamUpdate.queryParamValues.find((qp) => qp.name === name)?.newValue;

        const portCallGuid = getQueryParamValue('portCallGuid');
        const berthCallGuid = getQueryParamValue('berthCallGuid');
        const cargoOperationGuid = getQueryParamValue('cargoOperationGuid');

        console.log('[handleStowageCallQueryParamChanges] portCallGuid:', portCallGuid, '| filteredTree size:', filteredStowageRotationTree.length);

        if (portCallGuid) {
            const portCallItem = filteredStowageRotationTree.find((pc) => pc.portCallGuid === portCallGuid);

            if (!portCallItem) {
                // Port call not in filtered tree — check if it exists in the full rotation (different voyage)
                const rotationPortCall = activeStowagePlan?.rotation?.find((pc) => pc.portCallGuid === portCallGuid);
                if (rotationPortCall && selectedStowageVoyages?.length > 0) {
                    // Expand voyage selection to include this port call's voyage, then return.
                    // The tree will re-filter with the expanded selection and this handler will re-run.
                    const voyageNumber = rotationPortCall.voyageNumber;
                    if (!selectedStowageVoyages.includes(voyageNumber)) {
                        setSelectedStowageVoyages([...selectedStowageVoyages, voyageNumber].sort());
                    }
                    return;
                }
                return clearQueryParamsStowagePortCall();
            }

            if (!selectedStowageRotationPortCall || selectedStowageRotationPortCall.portCallGuid !== portCallGuid) {
                console.log('[handleStowageCallQueryParamChanges] Setting selected port call:', portCallItem.portDisplayName, `(voyage ${portCallItem.voyageNumber}, status=${portCallItem.portCallStatus}, voyageStatus=${portCallItem.voyageStatus})`);
                setSelectedStowageRotationPortCall({ ...portCallItem });
                if (!berthCallGuid) setSelectedStowageRotationBerthCall(null);
                if (!cargoOperationGuid) setSelectedCargoOperation(null);
            }

            const berthCallItem = portCallItem.berthCalls.find((bc) => bc.berthCallGuid === berthCallGuid);
            const cargoOperation = portCallItem.berthCalls
                .flatMap((bc) => bc.cargoOperations)
                .find((co) => co.cargoOperationGuid === cargoOperationGuid);
            if (berthCallGuid) {
                if (berthCallItem) {
                    setSelectedStowageRotationBerthCall({ ...berthCallItem });
                } else {
                    clearQueryParamsStowageBerthCall();
                }
            }
            if (cargoOperationGuid) {
                if (cargoOperation) {
                    selectCargoOperation(cargoOperation);
                } else {
                    clearQueryParamsStowageCargoOperation();
                }
            }
        } else {
            if (selectedStowageRotationPortCall) {
                setSelectedStowageRotationPortCall(null);
            }
            if (selectedStowageRotationBerthCall) setSelectedStowageRotationBerthCall(null);
            if (selectedCargoOperation) setSelectedCargoOperation(null);
        }

        if (!berthCallGuid && selectedStowageRotationBerthCall) {
            setSelectedStowageRotationBerthCall(null);
        }
        if (!cargoOperationGuid && selectedCargoOperation) {
            setSelectedCargoOperation(null);
        }
    };

    const handleStowagePlanGuidChange = async (stowagePlanGuid: string) => {
        if (!stowagePlans) return;

        // Skip reload only if the active plan and its derived rotation tree are both ready.
        // This happens when saveStowageData creates a child plan from master/published,
        // sets it as active, and then updates the URL. The plan is already fully loaded
        // locally — reloading from the backend would overwrite it with stale data
        // (the background save may not have completed yet).
        if (
            activeStowagePlan?.stowagePlanGuid === stowagePlanGuid &&
            activeStowagePlan?.rotation?.length > 0 &&
            stowageRotationTree !== null
        ) {
            return;
        }

        let stowagePlan = stowagePlans.find((sp) => sp.stowagePlanGuid === stowagePlanGuid && !sp.isDeleted);
        setSelectedStowageRotationBerthCall(null);
        setSelectedStowageRotationPortCall(null);
        if (stowagePlan) {
            if (stowagePlan.isNewStowagePlan) {
                // One-shot signal: plan was just created and populated locally.
                // Consume the flag and set the plan as active. On the first URL change
                // (immediately after copy), the early-exit guard above handles it.
                // This branch handles re-navigation: user went back to master, then
                // re-selected the draft — activeStowagePlan no longer matches, so we
                // need to set it here.
                stowagePlan.isNewStowagePlan = null;
                setActiveStowagePlan({ ...stowagePlan });
            } else {
                // Existing plan — fetch latest changes from backend
                const _stowagePlanChanges = await getStowagePlanChanges(stowagePlan.stowagePlanGuid, true);
                stowagePlan = cleanStowagePlanData(stowagePlan);
                const { updatedStowagePlan } = await setStowageDataInStowagePlan(_stowagePlanChanges, stowagePlan);
                stowagePlan = updatedStowagePlan;
                setActiveStowagePlan({ ...stowagePlan });
            }
        } else if (stowagePlans.filter((sp) => !sp.isDeleted).length === 0) {
            // If there are no plans available, clear the stowage plan GUID from URL and try to fetch plans
            clearQueryParamsStowagePlan();
            await getStowagePlans(selectedVessel.imoNumber);
        } else {
            setAppModalData({
                title: 'Stowage plan not found',
                message: React.createElement(StowagePlanNotFoundMessage, { vesselName: selectedVessel.vesselName }),
                actionButtonText: 'OK',
                isCloseDisabled: true,
                action: () => {
                    const availablePlans = stowagePlans.filter((sp) => !sp.isDeleted);
                    const nextPlanGuid = getMasterStowagePlan(availablePlans)?.stowagePlanGuid || availablePlans[0]?.stowagePlanGuid;
                    if (nextPlanGuid) {
                        setQueryParamStowagePlan(nextPlanGuid);
                    } else {
                        // Only clear if there are absolutely no plans available
                        clearQueryParamsStowagePlan();
                    }
                    setAppModalData(null);
                },
            });
        }
    };

    const setStowageDataInStowagePlan = async (
        stowagePlanChanges: StowagePlanChange[],
        stowagePlan: StowagePlan,
        updatedStowagePlans?: StowagePlan[],
        awaitHeavyComputation = true
    ) => {
        if (!selectedVessel) return;
        const _stowagePlans = updatedStowagePlans ?? stowagePlans;
        const { stowagePlanRotationPortCalls, violations, updatedStowagePlan } = getStowageDataItems(stowagePlanChanges, _stowagePlans, stowagePlan);

        setStowagePlanRotationPortCallList([...stowagePlanRotationPortCalls]);
        setStowagePlanRotationCargoList([...stowagePlan.cargoes]);

        setSavedViolations([...violations]);

        // Yield to browser so React can paint the state updates above before heavy computation
        await new Promise((resolve) => setTimeout(resolve, 0));

        if (awaitHeavyComputation) {
            await updateStowageData(updatedStowagePlan, _stowagePlans, stowagePlanRotationPortCalls, stowagePlan.cargoes, violations);
        } else {
            // Fire-and-forget: heavy computation (manifest rebuild, comparison) runs
            // asynchronously after the save promise resolves, keeping the UI responsive
            updateStowageData(updatedStowagePlan, _stowagePlans, stowagePlanRotationPortCalls, stowagePlan.cargoes, violations);
        }

        return { stowagePlanRotationPortCalls, updatedStowagePlan, violations };
    };

    return {
        handleStowagePlanGuidChange,
        handleStowageCallQueryParamChanges,
        setStowageDataInStowagePlan,
    };
};
